import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Button, DateRangePicker, PageHeader, Table } from '@demandlocal/components'
import LineChart from '../../../../../components/Charts/LineChart'
import { ReactComponent as DownloadIcon } from '../../../../../images/icons/action_download.svg'
import { ReactComponent as VehicleIcon } from '../../../../../images/icons/vehicle_single.svg'
import { useParams } from 'react-router'
import { createColumnHelper } from '@tanstack/react-table'
import PolarAreaChart from '../../../../../components/Charts/PolarAreaChart'
import { Link, useSearchParams } from 'react-router-dom'
import { useDownloadPDFReport } from '../../../../../api/dealership/reports/useDownloadPDFReport'
import toast from 'react-hot-toast'
import { useGetDisplayCampaignById } from '../../../../../api/dealership/campaigns/useGetDisplayCampaignById'
import { Vehicle } from '../../../../../lib/types/Vehicle'
import { DateRange } from '../../../../../lib/types/DateRange'
import { useGetSearchCampaignReports } from '../../../../../api/dealership/campaigns/reports/useGetSearchCampaignReports'
import BarChart from '../../../../../components/Charts/BarChart'
import { useGetSearchCampaignVehicleMetrics } from '../../../../../api/dealership/campaigns/reports/useGetInventoryCampaignVehicleMetrics'
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
} from '../../../../../lib/utils/formatNumber'
import GetSalesMatchback from '../../../../../components/Audiences/GetSalesMatchback'
import { GA4 } from '../../../../../lib/utils/ga4'
import { useDealership } from '../../../../../store/zustand/dealershipStore'
import mtm from '../../../../../lib/utils/mtm'
import { useDateRange } from '../../../../../store/zustand/dateRangeStore'
import downloadCSV from '../../../../../api/downloadCSV'
import { useGetCampaignBudget } from '../../../../../api/dealership/campaigns/useGetCampaignBudget'
import { useGetAgencyByAgencyId } from '../../../../../api/agencies/useGetAgencyByAgencyId'
import SearchConversionMetrics from '../../../../../components/Campaigns/Reports/SearchConversionMetrics'
import dayjs from 'dayjs'
import { useGetCampaignCallRecordings } from '../../../../../api/dealership/campaigns/reports/useGetCampaignCallRecordings'

const columnHelper = createColumnHelper<Vehicle>()
const callsColumnHelper = createColumnHelper<{
  phone_number: string
  recording: string
  datetime: string
  duration: string
  phone_call_vendor_id: string
}>()

const AudienceCampaignTemplate = ({
  children,
  filters,
  setFilters,
}: PropsWithChildren<{
  filters: DateRange
  setFilters: Dispatch<SetStateAction<DateRange>>
}>): JSX.Element => {
  const { campaign_id } = useParams()
  const { setDateRange } = useDateRange()
  const dealership = useDealership()
  const { data: campaign } = useGetDisplayCampaignById({ campaign_id: campaign_id })
  const { data: agency } = useGetAgencyByAgencyId(dealership?.organization_id)
  const { data: campaignBudget } = useGetCampaignBudget({
    campaign_id,
    filters: {
      start_date: filters?.dateRange.startDate,
      end_date: filters?.dateRange.endDate,
    },
  })
  const {
    data: {
      overallMetrics = {
        impressions: 0,
        clicks: 0,
        sales: 0,
        click_share: 0,
        search_impression_share: 0,
        phone_calls: 0,
        vendor_id: 0,
        search_absolute_top_impression_share: 0,
        search_impression_share_lost_to_budget: 0,
      },
    } = {},
    isLoading: isLoadingReport,
  } = useGetSearchCampaignReports({
    campaign_id: campaign_id,
    filters: {
      start_date: filters?.dateRange?.startDate,
      end_date: filters?.dateRange?.endDate,
    },
  })

  const { data: vehicles, isLoading: isLoadingVehicles } =
    useGetSearchCampaignVehicleMetrics({
      campaign_id: campaign_id,
      filters: {
        start_date: filters?.dateRange?.startDate,
        end_date: filters?.dateRange?.endDate,
      },
    })

  const { mutateAsync: downloadReport, isLoading: isDownloadingReport } =
    useDownloadPDFReport()

  useEffect(() => {
    if (!isLoadingReport && !isLoadingVehicles) {
      const content = document.getElementById('content')
      if (content) content.className = `${content.className} loaded`
    }
  }, [isLoadingReport, isLoadingVehicles])

  const onDateRangeChange = (dateRange: DateRange): void => {
    setFilters((prev: DateRange) => ({
      ...prev,
      ...dateRange,
    }))
    setDateRange(dateRange)
  }

  useEffect(() => {
    if (!isLoadingReport) {
      const content = document.getElementById('content')
      if (content) content.className = `${content.className} loaded`
    }
  }, [isLoadingReport])

  return (
    <div className={'flex flex-1 flex-col'} id={'content'}>
      <PageHeader
        backRoute={'../'}
        title={`${dealership?.dealership} - ${
          campaign?.client_campaign_display_name ?? ''
        }`}
        {...(Boolean(agency?.show_budget_type) && {
          infoPill: {
            type: 'warning',
            content: `Budget: ${
              agency?.show_budget_type === 1
                ? formatCurrency(campaignBudget?.demand_local_net)
                : formatCurrency(campaignBudget?.gross_revenue)
            }`,
          },
        })}
        cardsOptions={[
          {
            title: ({ collapsed }) => (
              <div>{collapsed ? 'CTR' : 'Click Through Rate'}</div>
            ),
            content: `${
              Math.round(
                (overallMetrics?.clicks / (overallMetrics?.impressions || 1)) * 10000,
              ) / 100
            }%`,
            color: 'bg-palatinate-blue/30',
          },
          {
            title: ({ collapsed }) => (
              <div>
                {collapsed
                  ? 'Imp Share Lost to $'
                  : 'Impression Share Lost due to Budget'}
              </div>
            ),
            content: formatPercentage(
              overallMetrics?.search_impression_share_lost_to_budget,
            ),
            color: 'bg-kelly-green/30',
          },
          {
            title: ({ collapsed }) => (
              <div>{collapsed ? 'Imp Share' : 'Search Impression Share'}</div>
            ),
            content: `${
              Math.round(overallMetrics?.search_impression_share * 10000) / 100
            }%`,
            color: 'bg-dodger-blue/30',
          },
          {
            title: ({ collapsed }) => <div>{collapsed ? 'Calls' : 'Phone calls'}</div>,
            content: formatNumber(overallMetrics?.phone_calls || 0),
            color: 'bg-secondary-malachite-green/30',
          },
          {
            title: ({ collapsed }) => {
              return collapsed ? (
                <div>Sales</div>
              ) : (
                <div className={'flex flex-row'}>
                  <span>Attributed sales</span>
                  <span className={'absolute right-0'}>
                    <VehicleIcon />
                  </span>
                </div>
              )
            },
            content: formatNumber(overallMetrics?.sales || 0),
            color: '#FFFFFF',
            border: true,
          },
        ]}
        rightSlot={({ hidden }) => (
          <div className={'print:hidden flex flex-row gap-2'}>
            <DateRangePicker
              collapsed={hidden}
              filters={filters}
              onChange={onDateRangeChange}
            />
            <Button
              disabled={isLoadingReport || isDownloadingReport}
              collapsed={hidden}
              leftSlot={<DownloadIcon className={'fill-white'} />}
              onClick={async (e) => {
                e.preventDefault()
                const toastId = toast.loading('Generating report...')
                GA4.sendEvent({
                  event_name: 'export_pdf',
                  event_params: {
                    campaign_type: 'INV',
                    campaign_id: campaign?.client_campaign_id,
                  },
                })
                mtm.sendEvent('Export', 'PDF', 'INV', campaign?.client_campaign_id)
                await downloadReport(undefined, {
                  async onSuccess(data) {
                    toast.success('Report generated successfully', { id: toastId })
                    const blob = await data.blob()
                    const url = window.URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `${campaign?.client_campaign_display_name}_${filters?.dateRange?.startDate}_-_${filters?.dateRange?.endDate}.pdf`
                    document.body.appendChild(a) // we need to append the element to the dom -> otherwise it will not work in firefox
                    a.click()
                    a.remove()
                  },
                })
              }}>
              Export as PDF
            </Button>
          </div>
        )}
      />
      <div className={'flex flex-1 flex-col px-10 max-w-[1920px] mx-auto w-full'}>
        <div className={'mt-12 mb-10'} />
        {children}
      </div>
    </div>
  )
}

const INVCampaign = (): JSX.Element => {
  const { campaign_id } = useParams()
  const { dateRange } = useDateRange()
  const [queryParams, setQueryParams] = useSearchParams()
  const dealership = useDealership()
  const [downloadingTable, setDownloadingTable] = useState<boolean>(false)
  const [downloadingCalls, setDownloadingCalls] = useState<boolean>(false)
  const { data: campaign } = useGetDisplayCampaignById({ campaign_id: campaign_id })

  const [filters, setFilters] = useState<DateRange>({
    dateRange: {
      startDate: queryParams.get('startDate') || dateRange.dateRange.startDate,
      endDate: queryParams.get('endDate') || dateRange.dateRange.endDate,
    },
  })

  const {
    data: { metrics } = { metrics: [] },
    data: { overallMetrics = { impressions: 0, clicks: 0, leads: 0, sales: 0 } } = {},
    data: { dayOfWeekData } = { dayOfWeekData: [] },
    data: { deviceMetrics } = { deviceMetrics: [] },
    data: { conversions } = { conversions: [] },
    isLoading: isLoadingReport,
    isPreviousData: isPreviousReportsData,
  } = useGetSearchCampaignReports({
    campaign_id: campaign_id,
    filters: {
      start_date: filters.dateRange.startDate,
      end_date: filters.dateRange.endDate,
    },
  })

  useEffect(() => {
    setQueryParams(filters.dateRange)
  }, [filters, setQueryParams])

  const phoneCallsColumns = useMemo(
    () => [
      callsColumnHelper.accessor('phone_number', {
        header: 'Caller',
        cell: (info) => info.getValue(),
      }),
      callsColumnHelper.accessor('phone_call_vendor_id', {
        header: 'Recording',
        cell: (info) => {
          return (
            <audio controls>
              <source
                src={`https://phone-call-recordings.s3.us-east-1.amazonaws.com/${info.getValue()}.mp3`}
                type="audio/mpeg"
              />
            </audio>
          )
        },
      }),
      callsColumnHelper.accessor('datetime', {
        header: 'Date',
        cell: (info) => dayjs(info.getValue()).format(`MMMM D, YYYY - h:mm a`),
      }),
      callsColumnHelper.accessor('duration', {
        header: 'Duration',
        cell: (info) => info.getValue(),
      }),
    ],
    [],
  )

  const vehicleMetricsColumns = useMemo(
    () => [
      columnHelper.accessor('description', {
        header: 'Vehicle',
        cell: ({ row }) => (
          <Link
            className={'text-primary-persian-blue font-bold'}
            to={`${
              row.original.vin_number
                ? `../../inventory/vehicles/${row.original.vin_number}`
                : '#'
            }`}>
            {row?.original?.description}
          </Link>
        ),
      }),
      columnHelper.accessor('vin_number', {
        header: 'VIN',
        cell: ({ getValue }) => getValue(),
      }),
      columnHelper.accessor('stock_number', {
        header: 'Stock',
        cell: ({ getValue }) => getValue(),
      }),

      columnHelper.accessor('impressions', {
        header: 'Impressions',
        cell: ({ getValue }) => formatNumber(getValue() ?? 0),
      }),
      columnHelper.accessor('clicks', {
        header: 'Clicks',
        cell: ({ getValue }) => formatNumber(getValue() ?? 0),
      }),
      columnHelper.accessor('ctr', {
        header: 'CTR',
        cell: ({ getValue }) => `${getValue()}%`,
      }),
    ],
    [],
  )

  return (
    <AudienceCampaignTemplate filters={filters} setFilters={setFilters}>
      <div className={'flex flex-col gap-10 pb-8'}>
        <div className={'flex flex-col gap-8'}>
          {overallMetrics.sales === 0 && !isLoadingReport && <GetSalesMatchback />}
          <div className={'flex grid grid-cols-2 gap-5'}>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>Total Impressions</span>
                <span>
                  {!isLoadingReport &&
                    new Intl.NumberFormat('en-US').format(
                      metrics
                        ?.map((metric: { impressions: number }) => metric.impressions)
                        .reduce((acc: number, cur: number) => acc + cur, 0),
                    )}
                </span>
              </div>
              <div className={'h-[270px] p-2'}>
                <LineChart
                  data={metrics ?? []}
                  gradient={'green-to-cyan'}
                  yKey={'impressions'}
                  isLoading={isLoadingReport}
                  isPreviousData={isPreviousReportsData}
                />
              </div>
            </div>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>Total Clicks</span>
                <span>
                  {!isLoadingReport &&
                    new Intl.NumberFormat('en-US').format(
                      metrics
                        ?.map((metric: { clicks: number }) => metric.clicks)
                        .reduce((acc: number, cur: number) => acc + cur, 0),
                    )}
                </span>
              </div>
              <div className={'h-[270px] p-2'}>
                <LineChart
                  data={metrics ?? []}
                  gradient={'red-to-orange'}
                  yKey={'clicks'}
                  isLoading={isLoadingReport}
                  isPreviousData={isPreviousReportsData}
                />
              </div>
            </div>
          </div>
          <div className={'flex grid grid-cols-2 gap-5'}>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>Impressions by day of week</span>
              </div>
              <div className={'h-[270px] p-2'}>
                <BarChart
                  data={dayOfWeekData?.map(({ day_of_week, impressions }) => {
                    return {
                      label: day_of_week,
                      value: impressions,
                    }
                  })}
                  isLoading={isLoadingReport}
                  isPreviousData={isPreviousReportsData}
                />
              </div>
            </div>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>Clicks by device</span>
              </div>
              <div className={'h-[270px] p-2'}>
                <PolarAreaChart
                  data={deviceMetrics?.map(
                    (data: { device_name: string; clicks: number }) => {
                      return {
                        label: data.device_name,
                        value: data.clicks,
                      }
                    },
                  )}
                  isPreviousData={isPreviousReportsData}
                  isLoading={isLoadingReport}
                />
              </div>
            </div>
          </div>
          <SearchConversionMetrics
            conversions={conversions}
            isLoading={isLoadingReport}
            isPreviousData={isPreviousReportsData}
          />
          <div className={'pb-5 border-2 rounded-lg flex flex-col gap-3'}>
            <div
              className={
                'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
              }>
              <span>CTR</span>
              <div className={'flex items-center gap-2'}>
                {metrics.length > 0 &&
                  `${
                    Math.round(
                      (metrics.map((item) => item.clicks).reduce((a, b) => a + b) *
                        10000) /
                        metrics.map((item) => item.impressions).reduce((a, b) => a + b),
                    ) / 100
                  }%`}
              </div>
            </div>
            <div className={'w-full h-[320px] p-2'}>
              <LineChart
                data={metrics ?? []}
                gradient={'blue-to-cyan'}
                yKey={'ctr'}
                isLoading={isLoadingReport}
                isPreviousData={isPreviousReportsData}
              />
            </div>
          </div>
          <div className={'flex flex-col gap-4'}>
            <div className="flex flex-row justify-between items-center">
              <span className={'font-bold text-primary-xiketic text-sm'}>
                Phone calls
              </span>
              <Button
                disabled={downloadingCalls}
                variant="primary"
                onClick={async (e) => {
                  e.preventDefault()
                  const toastId = toast.loading('Downloading...')
                  setDownloadingCalls(true)
                  try {
                    const headers = phoneCallsColumns.map((column) => ({
                      accessorKey:
                        // @ts-ignore
                        column.accessorKey === 'phone_call_vendor_id'
                          ? 'recording'
                          : // @ts-ignore
                            column.accessorKey,
                      header: column.header,
                    }))
                    await downloadCSV({
                      url: `/agencies/${dealership?.organization_id}/dealership/${dealership?.id}/campaigns/search/${campaign_id}/calls`,
                      fetchOptions: {
                        campaign_id: campaign_id,
                        start_date: filters?.dateRange?.startDate,
                        end_date: filters?.dateRange?.endDate,
                      },
                      headers: headers,
                      fileName: `${campaign.client_campaign_display_name}_phone_calls_${filters.dateRange.startDate}-${filters.dateRange.endDate}.csv`,
                    })
                    toast.success('Downloaded table!', { id: toastId, duration: 5000 })
                    setDownloadingCalls(false)
                  } catch (err) {
                    toast.error('Failed to download table at this time.', {
                      id: toastId,
                      duration: 5000,
                    })
                    setDownloadingCalls(false)
                  }
                }}>
                Export CSV
              </Button>
            </div>
            <Table
              emptyLabel={'No phone calls found'}
              columns={phoneCallsColumns}
              filters={{
                campaign_id: campaign_id,
                start_date: filters?.dateRange?.startDate,
                end_date: filters?.dateRange?.endDate,
              }}
              customPageSize={10}
              defaultSorting={[{ id: 'datetime', desc: true }]}
              fetchFn={useGetCampaignCallRecordings}
            />
          </div>
          <div className={'flex flex-col gap-4'}>
            <div className="flex flex-row justify-between items-center">
              <span className={'font-bold text-primary-xiketic text-sm'}>
                Top Performing Vehicles
              </span>
              <Button
                disabled={downloadingTable}
                variant="primary"
                onClick={async (e) => {
                  e.preventDefault()
                  const toastId = toast.loading('Downloading...')
                  setDownloadingTable(true)
                  try {
                    const headers = vehicleMetricsColumns.map((column) => ({
                      // @ts-ignore
                      accessorKey: column.accessorKey,
                      header: column.header,
                    }))
                    await downloadCSV({
                      url: `/agencies/${dealership?.organization_id}/dealership/${dealership?.id}/campaigns/search/${campaign_id}/vehicles`,
                      fetchOptions: {
                        campaign_id: campaign_id,
                        start_date: filters?.dateRange?.startDate,
                        end_date: filters?.dateRange?.endDate,
                      },
                      headers: headers,
                      fileName: `${campaign.client_campaign_display_name}_top_performing_vehicles_${filters.dateRange.startDate}-${filters.dateRange.endDate}.csv`,
                    })
                    toast.success('Downloaded table!', { id: toastId, duration: 5000 })
                    setDownloadingTable(false)
                  } catch (err) {
                    toast.error('Failed to download table at this time.', {
                      id: toastId,
                      duration: 5000,
                    })
                    setDownloadingTable(false)
                  }
                }}>
                Export CSV
              </Button>
            </div>
            <Table
              emptyLabel={'No vehicles found'}
              columns={vehicleMetricsColumns}
              filters={{
                campaign_id: campaign_id,
                start_date: filters?.dateRange?.startDate,
                end_date: filters?.dateRange?.endDate,
              }}
              defaultSorting={[
                {
                  id: 'impressions',
                  desc: true,
                },
              ]}
              customPageSize={10}
              fetchFn={useGetSearchCampaignVehicleMetrics}
            />
          </div>
        </div>
      </div>
    </AudienceCampaignTemplate>
  )
}

export default INVCampaign
